import React, { useState, useMemo, useCallback } from 'react';
import { useWeddingContext } from '../context/WeddingContext';
import FilterModal from './FilterModal';
import EditTaskModal from './EditTaskModal';
import DeleteConfirmModal from './DeleteConfirmModal';
import { Task, TaskStatus, TaskGroup } from '../types';

const TaskList: React.FC = () => {
  const { 
    updateTask, 
    getFilteredTasks, 
    getGroupedTasks,
    setCurrentView, 
    state, 
    tasks,
    updateFilterSettings 
  } = useWeddingContext();

  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [deletingTaskId, setDeletingTaskId] = useState<string | null>(null);

  // メモ化されたフィルター処理
  const memoizedFilteredTasks = useMemo(() => {
    return getFilteredTasks();
  }, [getFilteredTasks]);

  const memoizedGroupedTasks = useMemo(() => {
    return getGroupedTasks();
  }, [getGroupedTasks]);

  // メモ化されたイベントハンドラー
  const handleStatusChange = useCallback((taskId: string, newStatus: string) => {
    const task = tasks.find(t => (t.taskId || t.id) === taskId);
    if (task) {
      updateTask(taskId, { status: newStatus as TaskStatus });
    }
  }, [tasks, updateTask]);
/*
  const getStatusIcon = (status: string) => {
    switch (status) {
      case '未着手':
        return '⚪';
      case '進行中':
        return '🟡';
      case '完了':
        return '✅';
      default:
        return '⚪';
    }
  };
*/
  // 新しいステータスバッジ表示関数
  const getStatusBadge = (status: string) => {
    switch (status) {
      case '未着手':
        return {
          icon: '⚪',
          text: '未着手',
          bgColor: 'bg-gray-100',
          textColor: 'text-gray-700',
          borderColor: 'border-gray-300',
          hoverBg: 'hover:bg-gray-200'
        };
      case '進行中':
        return {
          icon: '🔄',
          text: '進行中',
          bgColor: 'bg-blue-100',
          textColor: 'text-blue-700',
          borderColor: 'border-blue-300',
          hoverBg: 'hover:bg-blue-200'
        };
      case '完了':
        return {
          icon: '✅',
          text: '完了',
          bgColor: 'bg-green-100',
          textColor: 'text-green-700',
          borderColor: 'border-green-300',
          hoverBg: 'hover:bg-green-200'
        };
      default:
        return {
          icon: '⚪',
          text: '未着手',
          bgColor: 'bg-gray-100',
          textColor: 'text-gray-700',
          borderColor: 'border-gray-300',
          hoverBg: 'hover:bg-gray-200'
        };
    }
  };

  // 次のステータスを取得する関数
  const getNextStatus = (currentStatus: string) => {
    switch (currentStatus) {
      case '未着手':
        return '進行中';
      case '進行中':
        return '完了';
      case '完了':
        return '未着手';
      default:
        return '進行中';
    }
  };

  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case '高':
        return '🔴';
      case '中':
        return '🟡';
      case '低':
        return '🟢';
      default:
        return '⚪';
    }
  };

  const getDueDateStatus = (dueDate: string) => {
    if (!dueDate) return '';
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(dueDate);
    due.setHours(0, 0, 0, 0);
    
    const diffTime = due.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      return 'overdue';
    } else if (diffDays === 0) {
      return 'today';
    } else if (diffDays <= 3) {
      return 'soon';
    }
    return 'normal';
  };

  const getDueDateClass = (dueDate: string) => {
    const status = getDueDateStatus(dueDate);
    switch (status) {
      case 'overdue':
        return 'text-red-600 font-semibold';
      case 'today':
        return 'text-orange-600 font-semibold';
      case 'soon':
        return 'text-yellow-600 font-semibold';
      default:
        return 'text-gray-600';
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return `${date.getMonth() + 1}/${date.getDate()}`;
  };

  // フィルター条件表示機能（要件書準拠）
  const getActiveFiltersDisplay = () => {
    const filters = [];
    const { filterSettings } = state;

    if (filterSettings.category !== 'all') {
      filters.push(`カテゴリ: ${filterSettings.category}`);
    }
    if (filterSettings.status !== 'all') {
      filters.push(`ステータス: ${filterSettings.status}`);
    }
    if (filterSettings.assignee !== 'all') {
      filters.push(`担当者: ${filterSettings.assignee === '未設定' ? '未設定' : filterSettings.assignee}`);
    }
    if (filterSettings.period !== 'all') {
      const periodLabels = {
        'overdue': '期限切れ',
        'today': '今日期限',
        'this_week': '今週期限',
        'this_month': '今月期限',
        'next_month': '来月期限',
        'no_due_date': '期限未設定',
        'custom_range': '日付範囲指定'
      };
      filters.push(`期間: ${periodLabels[filterSettings.period as keyof typeof periodLabels] || filterSettings.period}`);
    }
    if (filterSettings.hideCompleted) {
      filters.push('完了タスクを隠す');
    }

    return filters;
  };

  const removeFilter = (filterType: string) => {
    const newSettings = { ...state.filterSettings };
    
    switch (filterType) {
      case 'category':
        newSettings.category = 'all';
        break;
      case 'status':
        newSettings.status = 'all';
        break;
      case 'assignee':
        newSettings.assignee = 'all';
        break;
      case 'period':
        newSettings.period = 'all';
        break;
      case 'hideCompleted':
        newSettings.hideCompleted = false;
        break;
    }
    
    updateFilterSettings(newSettings);
  };

  // メモ化されたrenderTaskItem関数
  const renderTaskItem = useCallback((task: Task) => (
    <div
      key={task.taskId || task.id}
      id={`task-${task.taskId || task.id}`}
      className={`
        group bg-white rounded-lg shadow-sm border border-gray-200 p-3 mb-2
        hover:shadow-md hover:border-gray-300 transition-all duration-200
        ${task.status === '完了' ? 'task-status-completed opacity-75 bg-gray-50' : ''}
      `}
    >
      {/* メインコンテンツエリア - レスポンシブ対応 */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        
        {/* 左側：ステータス + タスク名 */}
        <div className="flex items-center gap-3 flex-1 min-w-0">
          {/* ステータスバッジ */}
          <button
            onClick={() => {
              const newStatus = getNextStatus(task.status);
              handleStatusChange(task.taskId || task.id, newStatus);
            }}
            className={`
              flex-shrink-0 px-2.5 py-1 rounded-full border text-xs font-medium
              transition-all duration-200 hover:scale-105 hover:shadow-sm
              ${getStatusBadge(task.status).bgColor}
              ${getStatusBadge(task.status).textColor}
              ${getStatusBadge(task.status).borderColor}
              ${getStatusBadge(task.status).hoverBg}
            `}
            title={`現在：${task.status} → クリックで「${getNextStatus(task.status)}」に変更`}
          >
            <span className="flex items-center gap-1">
              <span className="text-sm">{getStatusBadge(task.status).icon}</span>
              <span className="hidden sm:inline">{getStatusBadge(task.status).text}</span>
            </span>
          </button>

          {/* タスク名 */}
          <div className="flex-1 min-w-0">
            <h3 className={`
              font-semibold text-base leading-tight truncate
              ${task.status === '完了' ? 'line-through text-gray-500' : 'text-gray-900'}
            `}>
              {task.name}
            </h3>
          </div>
        </div>

        {/* 右側：担当者 + 期限 + アクションボタン */}
        <div className="flex items-center gap-3 flex-shrink-0">
          {/* 担当者情報 */}
          {task.assignee && (
            <span className="flex items-center bg-blue-50 text-blue-700 px-2.5 py-1 rounded-full text-xs font-medium">
              <span className="mr-1">👤</span>
              <span className="hidden sm:inline">{task.assignee}</span>
              <span className="sm:hidden">{task.assignee.slice(0, 2)}</span>
            </span>
          )}

          {/* 期限情報 */}
          {task.dueDate && (
            <div className="flex items-center gap-1">
              <span className={`text-xs px-2.5 py-1 rounded-full font-medium flex items-center gap-1 ${getDueDateClass(task.dueDate)}`}>
                <span>📅</span>
                <span>{formatDate(task.dueDate)}</span>
              </span>
              {getDueDateStatus(task.dueDate) === 'overdue' && (
                <span className="text-xs bg-red-100 text-red-800 px-2 py-0.5 rounded-full font-medium">
                  期限切れ
                </span>
              )}
              {getDueDateStatus(task.dueDate) === 'today' && (
                <span className="text-xs bg-orange-100 text-orange-800 px-2 py-0.5 rounded-full font-medium">
                  今日
                </span>
              )}
              {getDueDateStatus(task.dueDate) === 'soon' && (
                <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded-full font-medium">
                  近日
                </span>
              )}
            </div>
          )}

          {/* アクションボタン（ホバー時表示） */}
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-all duration-200">
            <button
              onClick={() => setEditingTask(task)}
              className="edit-task-button text-blue-600 hover:text-blue-800 hover:bg-blue-50 p-2 rounded-full transition-all duration-200 hover:scale-105"
              title="タスクを編集"
            >
              <span className="text-sm">✏️</span>
            </button>
            <button
              onClick={() => setDeletingTaskId(task.taskId || task.id)}
              className="delete-task-button text-red-600 hover:text-red-800 hover:bg-red-50 p-2 rounded-full transition-all duration-200 hover:scale-105"
              title="タスクを削除"
            >
              <span className="text-sm">🗑️</span>
            </button>
          </div>
        </div>
      </div>

      {/* 説明文（折りたたみ可能） */}
      {task.description && (
        <div className="mt-2 pt-2 border-t border-gray-100">
          <div className="text-xs text-gray-600 bg-gray-50 p-2 rounded border-l-2 border-gray-300">
            {task.description}
          </div>
        </div>
      )}
    </div>
  ), [handleStatusChange, getNextStatus, getStatusBadge, getDueDateClass, formatDate, setEditingTask, setDeletingTaskId]);

  const renderGroupedTasks = (groups: TaskGroup[]) => {
    // グループ化なしの場合
    if (state.filterSettings.grouping === 'none') {
      return (
        <div className="space-y-3">
          {groups[0]?.tasks.map((task, index) => 
            <div key={task.taskId || task.id || `task-${index}`}>
              {renderTaskItem(task)}
            </div>
          )}
        </div>
      );
    }

    // グループ化ありの場合
    return (
      <div className="space-y-6">
        {groups.map((group, index) => (
          <div key={index} className="group-section">
            {/* グループヘッダー（要件書準拠：class="task-group-header"） */}
            <div className="task-group-header sticky top-0 bg-gray-50 border-b border-gray-200 p-3 mb-4 rounded-t-lg">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                <span className="mr-2">📁</span>
                {group.title}
                <span className="ml-2 text-sm text-gray-500 font-normal">
                  ({group.tasks.length}件)
                </span>
              </h3>
            </div>
            
            {/* グループ内のタスク */}
            <div className="space-y-3 pl-4">
              {group.tasks.map(renderTaskItem)}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const activeFilters = getActiveFiltersDisplay();

  return (
    <div className="pb-24 md:pb-6">
      {/* ヘッダー（レスポンシブ対応） */}
      <div className="mb-6">
        {/* タイトルエリア */}
        <div className="mb-3 sm:mb-4">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-800">📝 タスク管理</h2>
          <p className="text-gray-600 text-sm mt-1">
            {memoizedFilteredTasks.length > 0 
              ? `${memoizedFilteredTasks.filter(t => t.status !== '完了').length}件の未完了タスクがあります`
              : 'タスクを追加して結婚式準備を始めましょう'
            }
          </p>
        </div>
        
        {/* ボタンエリア - モバイル最適化 */}
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
          <button
            id="open-filter-modal-button"
            onClick={() => setIsFilterModalOpen(true)}
            className="flex-1 sm:flex-none bg-gray-500 hover:bg-gray-600 text-white px-3 sm:px-4 py-2 rounded-lg transition duration-150 text-sm sm:text-base"
          >
            <span className="sm:hidden">🔍 フィルター</span>
            <span className="hidden sm:inline">🔍 フィルター・並び替え</span>
          </button>
          <button
            onClick={() => setCurrentView('add-task')}
            className="flex-1 sm:flex-none bg-pink-500 hover:bg-pink-600 text-white px-3 sm:px-4 py-2 rounded-lg transition duration-150 text-sm sm:text-base"
          >
            <span className="sm:hidden">➕ 追加</span>
            <span className="hidden sm:inline">➕ 新規追加</span>
          </button>
        </div>
      </div>

      {/* 適用中フィルター条件表示（要件書準拠：id="active-filters-display"） */}
      {activeFilters.length > 0 && (
        <div id="active-filters-display" className="mb-4">
          <p className="text-sm text-gray-600 mb-2">適用中のフィルター:</p>
          <div className="flex flex-wrap gap-2">
            {activeFilters.map((filter, index) => (
              <div
                key={index}
                className="bg-pink-100 text-pink-800 px-3 py-1 rounded-full text-sm flex items-center"
              >
                <span>{filter}</span>
                <button
                  className="remove-chip ml-2 text-pink-600 hover:text-pink-800"
                  onClick={() => {
                    if (filter.startsWith('カテゴリ:')) removeFilter('category');
                    else if (filter.startsWith('ステータス:')) removeFilter('status');
                    else if (filter.startsWith('担当者:')) removeFilter('assignee');
                    else if (filter.startsWith('期間:')) removeFilter('period');
                    else if (filter === '完了タスクを隠す') removeFilter('hideCompleted');
                  }}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* タスク一覧（要件書準拠：id="task-list-container"） */}
      <div id="task-list-container">
        {memoizedFilteredTasks.length === 0 ? (
          <div id="no-tasks-message" className="text-center py-12 text-gray-500">
            <p className="text-lg mb-2">📝 該当するタスクはありません</p>
            <p>フィルター条件を変更するか、新しいタスクを追加してください</p>
          </div>
        ) : (
          renderGroupedTasks(memoizedGroupedTasks)
        )}
      </div>

      {/* モーダル */}
      {isFilterModalOpen && (
        <FilterModal
          isOpen={isFilterModalOpen}
          onClose={() => setIsFilterModalOpen(false)}
        />
      )}

      {editingTask && (
        <EditTaskModal
          task={editingTask}
          onClose={() => setEditingTask(null)}
        />
      )}

      {deletingTaskId && (
        <DeleteConfirmModal
          taskId={deletingTaskId}
          onClose={() => setDeletingTaskId(null)}
        />
      )}
    </div>
  );
};

export default TaskList;
